import React, { useId } from 'react';
import { Tooltip } from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';

import styles from './InspectorRow.module.css';

/**
 * Une ligne de réglage de l'inspecteur, comme LabelledRow dans le classique :
 * libellé à gauche, contrôle à droite. `stacked` met le libellé au-dessus,
 * pour un contrôle trop large pour tenir à côté (galerie, liste, média,
 * nuancier).
 *
 * Les champs Mantine qui portent leur propre `label` (nombre, select, switch)
 * n'en ont pas besoin : le CSS de l'inspecteur leur donne la même disposition
 * (cf. INSPECTOR_ROWS_CSS).
 *
 * `htmlFor` relie le libellé à un contrôle unique. Sans lui, la ligne devient
 * un groupe nommé par son libellé : un <label> autour de plusieurs contrôles
 * renverrait chaque clic vers le premier. `as` sert à l'adaptateur de Puck
 * (overrides.fieldLabel), qui demande parfois un <label> englobant.
 */
export default function InspectorRow({
  label, htmlFor, layout = 'row', description, hint, as: Root = 'div', children,
}) {
  const labelId = useId();
  const wrapsControl = Root === 'label';
  const LabelElement = htmlFor && !wrapsControl ? 'label' : 'span';
  const groupProps = htmlFor || wrapsControl ? {} : { role: 'group', 'aria-labelledby': labelId };

  return (
    <Root {...groupProps}>
      <div className={layout === 'stacked' ? styles.stacked : styles.row}>
        {label ? (
          <LabelElement id={labelId} htmlFor={LabelElement === 'label' ? htmlFor : undefined} className={styles.label} data-inspector-label>
            {label}
            {hint ? (
              <Tooltip label={hint} multiline w={260} withArrow withinPortal>
                <span className={styles.hint} role="img" aria-label={hint}>
                  <FontAwesomeIcon icon={icon({ name: 'circle-info', style: 'light' })} />
                </span>
              </Tooltip>
            ) : null}
          </LabelElement>
        ) : null}
        <div className={styles.control}>{children}</div>
      </div>
      {description ? <div className={styles.description}>{description}</div> : null}
    </Root>
  );
}

/**
 * `overrides.fieldLabel` : les types natifs de Puck passent par la même ligne.
 * Leur icône (lucide) est ignorée : le classique n'en met aucune.
 */
export function PuckInspectorFieldLabel({ label, el = 'label', children }) {
  return (
    <InspectorRow label={label} as={el} layout="stacked">
      {children}
    </InspectorRow>
  );
}
